import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useWorkflows } from "../hooks/useWorkflows";

const CATEGORIES = ["All", "Sales", "Customer Success", "Hiring", "Support", "Research", "Events", "Marketing"];

const INTEGRATION_META = {
    slack: { label: "Slack", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300", icon: "💬" },
    email: { label: "Email", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300", icon: "✉️" },
    webhook: { label: "Webhook", color: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300", icon: "🔗" },
    apollo: { label: "Apollo", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300", icon: "🚀" },
    notion: { label: "Notion", color: "bg-stone-100 text-stone-700 dark:bg-stone-700/40 dark:text-stone-300", icon: "📄" }
};

const STEP_TYPE_LABELS = {
    "ai.classify": { label: "AI Classify", color: "bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-700 text-violet-700 dark:text-violet-400" },
    "ai.score": { label: "AI Score", color: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-400" },
    "ai.draft_email": { label: "AI Draft Email", color: "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-400" },
    "notify.slack": { label: "Slack Notify", color: "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-400" },
    "notify.email": { label: "Send Email", color: "bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-700 text-teal-700 dark:text-teal-400" },
    "webhook.post": { label: "Webhook", color: "bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300" },
    "tag.apply": { label: "Apply Tag", color: "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400" },
    "if": { label: "Condition", color: "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700 text-orange-700 dark:text-orange-400" }
};

function IntegrationBadge({ integration }) {
    const meta = INTEGRATION_META[integration] || { label: integration, color: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300", icon: "⚙️" };
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${meta.color}`}>
            <span>{meta.icon}</span>
            {meta.label}
        </span>
    );
}

function StepPill({ type }) {
    const meta = STEP_TYPE_LABELS[type] || { label: type, color: "bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300" };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs ${meta.color}`}>
            {meta.label}
        </span>
    );
}

function RecipeCard({ recipe, onPreview, onInstall, installing }) {
    const steps = recipe.workflowJson?.steps || [];
    return (
        <div className="bg-[#ffffff] dark:bg-gray-800 rounded-xl border-[1px] border-gray-200 dark:border-gray-700 p-5 flex flex-col gap-3 hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-700 transition-all group">
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                    <div className="text-2xl w-10 h-10 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded-xl shrink-0">
                        {recipe.icon}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white leading-snug">{recipe.name}</h3>
                            {recipe.featured && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                    ⭐ Featured
                                </span>
                            )}
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">{recipe.category}</span>
                    </div>
                </div>
                {recipe.installCount > 0 && (
                    <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 shrink-0">{recipe.installCount} installs</span>
                )}
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{recipe.description}</p>

            {/* Step chain preview */}
            <div className="flex flex-wrap gap-1">
                {steps.slice(0, 5).map((s, i) => (
                    <StepPill key={i} type={s.type} />
                ))}
                {steps.length > 5 && (
                    <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">+{steps.length - 5} more</span>
                )}
            </div>

            {/* Integration badges */}
            {recipe.requiredIntegrations?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {recipe.requiredIntegrations.map(i => (
                        <IntegrationBadge key={i} integration={i} />
                    ))}
                </div>
            )}

            {/* Cost hint */}
            {recipe.avgCostPerRunCents > 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">~${(recipe.avgCostPerRunCents / 100).toFixed(3)} per run</p>
            )}

            <div className="flex gap-2 mt-auto pt-1">
                <button
                    onClick={() => onPreview(recipe)}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                    Preview
                </button>
                <button
                    onClick={() => onInstall(recipe)}
                    disabled={installing === recipe._id}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg transition-colors"
                >
                    {installing === recipe._id ? "Installing…" : "Install"}
                </button>
            </div>
        </div>
    );
}

function StepPreview({ steps }) {
    return (
        <div className="space-y-2">
            {steps.map((step, i) => {
                const meta = STEP_TYPE_LABELS[step.type] || { label: step.type, color: "bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300" };
                return (
                    <div key={i} className="flex items-start gap-3">
                        <div className="flex flex-col items-center shrink-0">
                            <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-semibold text-gray-500 dark:text-gray-400">
                                {i + 1}
                            </div>
                            {i < steps.length - 1 && <div className="w-px h-5 bg-gray-200 dark:bg-gray-600 mt-1" />}
                        </div>
                        <div className={`flex-1 rounded-lg border px-3 py-2 ${meta.color} text-xs mb-2`}>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-semibold">{step.name || step.type}</span>
                                <span className="opacity-60">{meta.label}</span>
                            </div>
                            {step.config?.rubric && (
                                <p className="opacity-70 mt-0.5 leading-relaxed">{step.config.rubric.slice(0, 120)}{step.config.rubric.length > 120 ? "…" : ""}</p>
                            )}
                            {step.config?.categories && (
                                <p className="opacity-70 mt-0.5">Categories: {step.config.categories.join(", ")}</p>
                            )}
                            {step.config?.message && (
                                <p className="opacity-70 mt-0.5 font-mono text-[10px] break-all">{step.config.message.slice(0, 100)}{step.config.message.length > 100 ? "…" : ""}</p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function PreviewModal({ recipe, formId, onClose, onInstall, installing }) {
    const steps = recipe.workflowJson?.steps || [];
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-[#ffffff] dark:bg-[#23263a] rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col font-Rajdhani">
                {/* Header */}
                <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="text-2xl w-10 h-10 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded-xl">
                            {recipe.icon}
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-gray-900 dark:text-white">{recipe.name}</h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">{recipe.category} · {steps.length} steps</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4 dark:text-white">
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{recipe.description}</p>

                    {recipe.requiredIntegrations?.length > 0 && (
                        <div className="bg-amber-50 dark:bg-amber-900/20 border-[1px] border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2.5">
                            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1.5">Requires configuration</p>
                            <div className="flex flex-wrap gap-1.5">
                                {recipe.requiredIntegrations.map(i => <IntegrationBadge key={i} integration={i} />)}
                            </div>
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">After installing, open the workflow editor to add your webhook URLs or credentials.</p>
                        </div>
                    )}

                    <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Workflow Steps</p>
                        <StepPreview steps={steps} />
                    </div>

                    {recipe.avgCostPerRunCents > 0 && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">Estimated AI cost: ~${(recipe.avgCostPerRunCents / 100).toFixed(3)} per run</p>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-700 shrink-0">
                    <button
                        onClick={() => onInstall(recipe)}
                        disabled={installing === recipe._id}
                        className="w-full py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-xl transition-colors"
                    >
                        {installing === recipe._id ? "Installing…" : `Install "${recipe.name}"`}
                    </button>
                </div>
            </div>
        </div>
    );
}

function SuccessBanner({ recipeName, workflowId, formId, onDismiss }) {
    const navigate = useNavigate();
    return (
        <div className="fixed bottom-6 right-6 z-50 bg-green-600 text-white rounded-xl shadow-2xl px-5 py-4 max-w-sm flex items-start gap-3">
            <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            <div className="flex-1">
                <p className="font-semibold text-sm">"{recipeName}" installed!</p>
                <p className="text-xs text-green-100 mt-0.5">Open the editor to configure your integrations.</p>
                <button
                    onClick={() => navigate(`/form-builder/${formId}/workflows/${workflowId}`)}
                    className="mt-2 text-xs font-semibold underline text-white hover:text-green-100"
                >
                    Open workflow editor →
                </button>
            </div>
            <button onClick={onDismiss} className="text-green-200 hover:text-white shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
}

export default function RecipeLibrary() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { listRecipes, installRecipe, loading, error } = useWorkflows();
    const [recipes, setRecipes] = useState([]);
    const [category, setCategory] = useState("All");
    const [sort, setSort] = useState("popular");
    const [search, setSearch] = useState("");
    const [previewRecipe, setPreviewRecipe] = useState(null);
    const [installing, setInstalling] = useState(null);
    const [successInfo, setSuccessInfo] = useState(null);
    const [installError, setInstallError] = useState(null);

    const load = useCallback(async (cat, s) => {
        const data = await listRecipes(cat === "All" ? undefined : cat, s);
        if (data) setRecipes(data);
    }, []); // eslint-disable-line

    useEffect(() => { load(category, sort); }, [category, sort]); // eslint-disable-line

    const filtered = search.trim()
        ? recipes.filter(r =>
            r.name.toLowerCase().includes(search.toLowerCase()) ||
            r.description.toLowerCase().includes(search.toLowerCase()) ||
            r.heroUseCase?.toLowerCase().includes(search.toLowerCase())
        )
        : recipes;

    const handleInstall = async (recipe) => {
        setInstallError(null);
        setInstalling(recipe._id);
        const result = await installRecipe(formId, recipe._id);
        setInstalling(null);
        if (result) {
            setPreviewRecipe(null);
            setSuccessInfo({ recipeName: recipe.name, workflowId: result.workflow._id });
        } else {
            setInstallError("Failed to install. Check your plan limits.");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#181d33] font-Rajdhani dark:text-white">
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(`/form-builder/${formId}/workflows`)}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Recipe Library</h1>
                            <p className="text-base text-gray-500 dark:text-gray-400 mt-0.5">Install pre-built workflows in one click</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={sort}
                            onChange={e => setSort(e.target.value)}
                            className="text-sm border-[1px] border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-[#ffffff] dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="popular">Most Popular</option>
                            <option value="featured">Featured</option>
                            <option value="newest">Newest</option>
                        </select>
                    </div>
                </div>

                {/* Search */}
                <div className="relative mb-5">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search recipes…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 text-sm border-[1px] border-gray-200 dark:border-gray-600 rounded-xl bg-[#ffffff] dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>

                {/* Category tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {CATEGORIES.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setCategory(cat)}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                category === cat
                                    ? "bg-indigo-600 text-white"
                                    : "bg-[#ffffff] dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-[1px] border-gray-200 dark:border-gray-600 hover:border-indigo-300"
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {installError && (
                    <div className="mb-4 bg-red-50 dark:bg-red-900/20 border-[1px] border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm flex items-center justify-between">
                        {installError}
                        <button onClick={() => setInstallError(null)} className="text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-300">✕</button>
                    </div>
                )}

                {/* Grid */}
                {loading && recipes.length === 0 ? (
                    <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-500 gap-2 text-sm">
                        <div className="animate-spin w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full" />
                        Loading recipes…
                    </div>
                ) : error ? (
                    <div className="bg-red-50 dark:bg-red-900/20 border-[1px] border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-xl px-4 py-3 text-sm">{error}</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-gray-400 dark:text-gray-500 text-base">No recipes found. Try a different search or category.</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filtered.map(recipe => (
                            <RecipeCard
                                key={recipe._id}
                                recipe={recipe}
                                onPreview={setPreviewRecipe}
                                onInstall={handleInstall}
                                installing={installing}
                            />
                        ))}
                    </div>
                )}

                {/* Footer hint */}
                <div className="mt-10 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">
                        Each recipe is a starting point — open the workflow editor after installing to customize steps and connect your integrations.
                    </p>
                </div>
            </div>

            {previewRecipe && (
                <PreviewModal
                    recipe={previewRecipe}
                    formId={formId}
                    onClose={() => setPreviewRecipe(null)}
                    onInstall={handleInstall}
                    installing={installing}
                />
            )}

            {successInfo && (
                <SuccessBanner
                    recipeName={successInfo.recipeName}
                    workflowId={successInfo.workflowId}
                    formId={formId}
                    onDismiss={() => setSuccessInfo(null)}
                />
            )}
        </div>
    );
}
